import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Button,
  Input,
  Select,
  DatePicker,
  Table,
  InputNumber,
  Row,
  Col,
  Typography,
  App,
  Spin,
  Radio,
  Space,
  Alert,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import { usePurchaseRequisition, useCreatePurchaseRequisition, useUpdatePurchaseRequisition } from '@/hooks/usePurchaseRequisition';
import { useSalesOrders } from '@/hooks/useSalesOrder';
import type { Item } from '@/services/inventory.service';
import { useUomUnits } from '@/hooks/useInventory';
import { ItemSelect, formatCatalogItemDescription, formatItemDescriptionWithProfile, type ItemLike } from '@/components/inventory/ItemSelect';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import { CostControlLineCell } from '@/components/project/CostControlLineCell';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import purchaseRequisitionService, { type EligibleSOLine, type CreationMode } from '@/services/purchaseRequisition.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { headerProjectLabel } from '@/utils/projectDisplay';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { LineSpecificationsText, lineSpecificationsExpandable } from '@/components/procurement/LineSpecifications';
import { DOCUMENT_ENTITY_TYPE_PURCHASE_REQUISITION } from '@/constants/document-entity-types';
import { useQuery } from '@tanstack/react-query';
import engineeringMaterialService, {
  formatKg,
  parseEngineeringSnapshot,
  type EngineeringLineInput,
  type EngineeringLineSnapshot,
} from '@/services/engineeringMaterial.service';
import { PrLineEngineeringPanel } from '@/components/procurement/PrLineEngineeringPanel';
import {
  MEASUREMENT_DIMENSION_LABELS,
  MEASUREMENT_TYPE_LABELS,
  computeMeasuredQuantity,
  expandQuantityByLength,
  isLinearLengthUom,
  lengthInLineUom,
  measuredQuantityWarning,
  measurementDimensionKeys,
  normalizeMeasurementType,
  parseDimensionToMm,
  pieceQuantityFromOrderQty,
  type MeasurementDimensionKey,
  type MeasurementType,
} from '@/utils/inventory/measurementQuantity';

const { Title, Text, Paragraph } = Typography;

function getItemUomOptions(item: Pick<Item, 'base_uom' | 'purchase_uom' | 'sales_uom'>): string[] {
  const uoms = [item.base_uom, item.purchase_uom, item.sales_uom].filter(Boolean) as string[];
  return [...new Set(uoms)];
}

const FALLBACK_UOM_CODES = ['PCS', 'SET', 'EA', 'NOS', 'KG', 'M', 'MM', 'SQM', 'L', 'HR', 'LOT'];

function resolveLineKind(it: {
  line_kind?: string | null;
  item_type?: string | null;
}): 'catalog' | 'description_only' {
  if (it.line_kind === 'description_only') return 'description_only';
  if (it.line_kind === 'catalog') return 'catalog';
  // Legacy rows without line_kind
  return it.item_type === 'Non-Stock' ? 'description_only' : 'catalog';
}

interface DirectLine {
  key: string;
  /** Persisted PR line id (edit) — sent on update so backend can upsert without breaking FKs */
  id?: string;
  line_kind: 'catalog' | 'description_only';
  item_id?: string;
  item_code?: string;
  /** Item Master name. Description-only lines have none; the typed description is shown instead. */
  item_name?: string;
  item_description: string;
  requested_quantity: number;
  uom?: string;
  uomOptions?: string[];
  /** Legacy free-text length (lines saved before measurement types) */
  length?: string;
  /** Item measurement type; when set, requested_quantity is pieces and dims drive Total Qty */
  measurement_type?: MeasurementType | null;
  length_mm?: number | null;
  width_mm?: number | null;
  height_mm?: number | null;
  estimated_unit_price?: number;
  project_id: string;
  wbs_node_id?: string | null;
  cbs_code_id?: string | null;
  cost_category?: string | null;
  /** Item-wise technical specification (defaults from the item master) */
  specifications?: string;
  notes?: string;
  is_critical?: boolean;
  profile_grade?: string;
  profile_heat_no?: string;
  profile_dimension?: string;
  profile_length?: string;
  /** Calculator inputs changed in this session; omitted on save when untouched (backend keeps prior inputs). */
  engineering_input?: EngineeringLineInput;
  /** Snapshot saved on the line (frozen definition + inputs). */
  engineering_saved?: EngineeringLineSnapshot | null;
  /** Theoretical weight (kg): saved value, or the latest preview. Recomputed by the server on save. */
  theoretical_weight?: number | null;
}

const WEIGHT_INPUT_FIELDS = new Set<keyof DirectLine>([
  'requested_quantity',
  'uom',
  'length',
  'length_mm',
  'width_mm',
  'height_mm',
  'engineering_input',
]);

/** Total order quantity for a line in its UOM (measured → pieces × factor; legacy → Length × Qty). */
function lineOrderQuantity(r: DirectLine): number {
  const pieces = Number(r.requested_quantity || 0);
  if (r.measurement_type) {
    return computeMeasuredQuantity({
      type: r.measurement_type,
      pieces,
      dims: { length_mm: r.length_mm, width_mm: r.width_mm, height_mm: r.height_mm },
      uom: r.uom,
    }).total;
  }
  return expandQuantityByLength(pieces, r.length, r.uom);
}

function lineSeedItem(r: DirectLine): ItemLike | undefined {
  if (!r.item_id) return undefined;
  return {
    id: r.item_id,
    item_code: r.item_code || '',
    item_name: r.item_name || r.item_code || '',
    item_description: r.item_description,
    item_type: undefined,
    specifications: undefined,
    profile_grade: r.profile_grade,
    profile_heat_no: r.profile_heat_no,
    profile_dimension: r.profile_dimension,
    profile_length: r.profile_length,
    is_critical: r.is_critical,
    base_uom: r.uom,
    purchase_uom: r.uom,
    sales_uom: r.uom,
  } as unknown as ItemLike;
}

export const PurchaseRequestFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const isNew = id === 'new' || !id;
  const isEdit = !isNew && id;

  const [form] = Form.useForm();
  const [creationMode, setCreationMode] = useState<CreationMode>('Direct');
  useApplyUserDefaultDepartment(form, isNew);
  const [directLines, setDirectLines] = useState<DirectLine[]>([]);
  const [nonstockDefaultCode, setNonstockDefaultCode] = useState('NS-CONSUMABLE');
  const [selectedSoIds, setSelectedSoIds] = useState<string[]>([]);
  const [selectedFromSoLines, setSelectedFromSoLines] = useState<{ line: EligibleSOLine; requested_quantity: number }[]>([]);

  const { data: pr, isLoading: prLoading } = usePurchaseRequisition(isEdit ? id : undefined);
  const { data: uomMasterRows = [] } = useUomUnits();
  const masterUomCodes = useMemo(() => {
    const set = new Set<string>(FALLBACK_UOM_CODES);
    for (const row of uomMasterRows) {
      const code = String(row.code || '').trim();
      if (code && row.is_active !== false) set.add(code);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [uomMasterRows]);
  const { baseCurrency, allCurrencies, loading: currenciesLoading } = useCurrency();
  const selectedCurrency = Form.useWatch('currency', form) ?? baseCurrency?.currency_code ?? 'AED';
  const currencyOptions = useMemo(() => {
    const active = (allCurrencies || []).filter((c) => c.is_active);
    if (active.length > 0) {
      return active.map((c) => {
        const d = uiPlainCurrencyLabel(c.symbol, c.currency_code);
        const label =
          d === c.currency_code ? `${c.currency_code} (${c.currency_name})` : `${d} ${c.currency_code}`;
        return { value: c.currency_code, label };
      });
    }
    const base = baseCurrency?.currency_code ?? 'AED';
    return [{ value: base, label: baseCurrency?.currency_name ? `${base} (${baseCurrency.currency_name})` : base }];
  }, [allCurrencies, baseCurrency]);
  const canListSalesOrders =
    !!user?.is_super_admin ||
    (user?.permissions ?? []).some((p) =>
      ['growth.order.view', 'growth.view', 'procurement.view', 'procurement.pr.view', 'procurement.pr.create', 'procurement.po.view'].includes(p)
    );
  const { data: salesOrders = [] } = useSalesOrders({}, { enabled: creationMode === 'AgainstSalesOrder' && canListSalesOrders });
  const canOverrideEngineering =
    !!user?.is_super_admin || (user?.permissions ?? []).includes('procurement.pr.engineering_override');
  const { data: engineeringConfig } = useQuery({
    queryKey: ['engineering', 'config'],
    queryFn: () => engineeringMaterialService.getConfig(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const engineeringOn = Boolean(engineeringConfig?.available && engineeringConfig.enabled);
  const catalogCodes = useMemo(
    () =>
      [...new Set(directLines.filter((l) => l.line_kind === 'catalog' && l.item_code).map((l) => l.item_code!))].sort(),
    [directLines]
  );
  const { data: approvedEngineering = {} } = useQuery({
    queryKey: ['engineering', 'approved', catalogCodes],
    queryFn: () => engineeringMaterialService.getApprovedByCodes(catalogCodes),
    enabled: engineeringOn && catalogCodes.length > 0,
    staleTime: 60_000,
  });
  const hasEngineering = (r: DirectLine) =>
    engineeringOn &&
    r.line_kind === 'catalog' &&
    Boolean(r.item_code) &&
    Boolean(approvedEngineering[r.item_code!] || (r.engineering_saved && r.engineering_saved.definition.item_code === r.item_code));
  const [eligibleLines, setEligibleLines] = useState<EligibleSOLine[]>([]);
  const [loadingEligible, setLoadingEligible] = useState(false);

  const createMutation = useCreatePurchaseRequisition();
  const updateMutation = useUpdatePurchaseRequisition();

  useEffect(() => {
    purchaseRequisitionService
      .getNonstockDefaultItem()
      .then((r) => {
        if (r?.item_code) setNonstockDefaultCode(r.item_code);
      })
      .catch(() => {
        /* keep NS-CONSUMABLE fallback */
      });
  }, []);

  useEffect(() => {
    if (isEdit && pr) {
      form.setFieldsValue({
        pr_date: pr.pr_date ? dayjs(pr.pr_date) : undefined,
        required_date: pr.required_date ? dayjs(pr.required_date) : undefined,
        priority: pr.priority,
        currency: pr.currency ?? baseCurrency?.currency_code ?? 'AED',
        department: pr.department,
        department_id: (pr as any).department_id ?? undefined,
        notes: pr.notes,
      });
      if (pr.creation_mode === 'Direct' && pr.items?.length) {
        setCreationMode('Direct');
        setDirectLines(
          pr.items.map((it, i) => {
            const kind = resolveLineKind(it);
            const masterUom =
              it.uom ?? it.base_uom ?? it.purchase_uom ?? it.sales_uom ?? undefined;
            const uomOpts = getItemUomOptions({
              base_uom: it.base_uom ?? it.uom ?? undefined,
              purchase_uom: it.purchase_uom ?? undefined,
              sales_uom: it.sales_uom ?? undefined,
            } as Item);
            return {
              key: it.id ?? String(i),
              id: it.id,
              line_kind: kind,
              item_id: kind === 'catalog' ? it.item_id || undefined : undefined,
              item_code: it.item_code,
              item_name: kind === 'catalog' ? it.item_name || undefined : undefined,
              item_description: formatItemDescriptionWithProfile(
                it.item_description ?? it.item_name ?? '',
                {
                  item_name: it.item_name,
                  profile_grade: it.profile_grade,
                  profile_heat_no: it.profile_heat_no,
                  profile_dimension: it.profile_dimension,
                }
              ),
              ...(() => {
                const snapshot = normalizeMeasurementType(it.measurement_type);
                if (kind === 'catalog' && snapshot && it.piece_quantity != null) {
                  return {
                    measurement_type: snapshot,
                    requested_quantity: Number(it.piece_quantity),
                    length_mm: it.length_mm != null ? Number(it.length_mm) : null,
                    width_mm: it.width_mm != null ? Number(it.width_mm) : null,
                    height_mm: it.height_mm != null ? Number(it.height_mm) : null,
                    length: '',
                  };
                }
                return {
                  measurement_type: null,
                  requested_quantity: pieceQuantityFromOrderQty(
                    Number(it.requested_quantity),
                    it.length,
                    masterUom
                  ),
                  length: it.length ?? '',
                };
              })(),
              uom: masterUom,
              uomOptions:
                kind === 'description_only'
                  ? masterUomCodes
                  : uomOpts.length
                    ? uomOpts
                    : masterUom
                      ? [masterUom]
                      : masterUomCodes,
              estimated_unit_price: Number(it.estimated_unit_price) || undefined,
              project_id: it.project_id ?? '',
              wbs_node_id: (it as any).wbs_node_id ?? null,
              cbs_code_id: (it as any).cbs_code_id ?? null,
              cost_category: (it as any).cost_category || 'Material',
              specifications: it.specifications ?? '',
              notes: it.notes,
              is_critical: Boolean(it.is_critical),
              profile_grade: it.profile_grade ?? undefined,
              profile_heat_no: it.profile_heat_no ?? undefined,
              profile_dimension: it.profile_dimension ?? undefined,
              profile_length: it.profile_length ?? undefined,
              engineering_saved: parseEngineeringSnapshot((it as any).engineering_snapshot),
              theoretical_weight:
                (it as any).theoretical_weight != null ? Number((it as any).theoretical_weight) : null,
            };
          })
        );
      }
      if (pr.creation_mode === 'AgainstSalesOrder' && pr.items?.length) {
        setCreationMode('AgainstSalesOrder');
        const soIds = [...new Set(pr.items.map((it) => it.sales_order_id).filter(Boolean))] as string[];
        setSelectedSoIds(soIds);
        setSelectedFromSoLines(
          pr.items.map((it) => {
            const line: EligibleSOLine = {
              sales_order_id: it.sales_order_id!,
              sales_order_number: (it as any).salesOrder?.sales_order_number ?? '',
              sales_order_line_id: it.sales_order_line_id!,
              line_number: (it as any).salesOrderItem?.line_number ?? 0,
              item_code: it.item_code ?? null,
              item_description: it.item_description,
              ordered_quantity: 0,
              requested_quantity: 0,
              requestable_quantity: Number(it.requested_quantity),
              uom: it.uom ?? null,
              project_id: it.project_id ?? null,
            };
            return { line, requested_quantity: Number(it.requested_quantity) };
          })
        );
      }
    }
  }, [isEdit, pr, form, baseCurrency?.currency_code]);

  useEffect(() => {
    if (!masterUomCodes.length) return;
    setDirectLines((prev) => {
      let changed = false;
      const next = prev.map((l) => {
        if (l.line_kind !== 'description_only') return l;
        const needsOptions = !l.uomOptions?.length;
        const needsDefault = !l.uom;
        if (!needsOptions && !needsDefault) return l;
        changed = true;
        return {
          ...l,
          uom: l.uom || (masterUomCodes.includes('PCS') ? 'PCS' : masterUomCodes[0]),
          uomOptions: masterUomCodes,
        };
      });
      return changed ? next : prev;
    });
  }, [masterUomCodes]);

  useEffect(() => {
    if (selectedSoIds.length === 0) {
      setEligibleLines([]);
      return;
    }
    setLoadingEligible(true);
    purchaseRequisitionService
      .getEligibleSoLines({ sales_order_ids: selectedSoIds })
      .then(setEligibleLines)
      .finally(() => setLoadingEligible(false));
  }, [selectedSoIds.join(',')]);

  const addDirectLine = (kind: 'catalog' | 'description_only' = 'catalog') => {
    const defaultUom =
      kind === 'description_only'
        ? masterUomCodes.includes('PCS')
          ? 'PCS'
          : masterUomCodes[0] || 'PCS'
        : undefined;
    setDirectLines((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}-${kind}`,
        line_kind: kind,
        item_code: kind === 'description_only' ? nonstockDefaultCode : undefined,
        item_description: '',
        requested_quantity: 0,
        project_id: '',
        length: '',
        uom: defaultUom,
        uomOptions: kind === 'description_only' ? masterUomCodes : undefined,
      },
    ]);
  };

  const removeDirectLine = (key: string) => {
    setDirectLines((prev) => prev.filter((l) => l.key !== key));
  };

  const updateDirectLine = (key: string, field: keyof DirectLine, value: any) => {
    const invalidatesWeight = WEIGHT_INPUT_FIELDS.has(field);
    setDirectLines((prev) =>
      prev.map((l) =>
        l.key === key ? { ...l, [field]: value, ...(invalidatesWeight ? { theoretical_weight: null } : {}) } : l
      )
    );
  };

  const applyCatalogItem = (key: string, item: Item) => {
    const uomOptions = getItemUomOptions(item);
    const defaultUom = item.base_uom || item.purchase_uom || item.sales_uom || (uomOptions[0] ?? '');
    setDirectLines((prev) =>
      prev.map((l) =>
        l.key === key
          ? {
              ...l,
              line_kind: 'catalog',
              item_id: item.id,
              item_code: item.item_code,
              item_name: item.item_name || undefined,
              item_description: formatCatalogItemDescription(item),
              uom: defaultUom,
              uomOptions,
              specifications:
                l.item_id === item.id && l.specifications?.trim()
                  ? l.specifications
                  : item.specifications?.trim() || l.specifications || '',
              is_critical: isItemCritical(item),
              profile_grade: item.profile_grade || undefined,
              profile_heat_no: item.profile_heat_no || undefined,
              profile_dimension: item.profile_dimension || undefined,
              profile_length: item.profile_length || undefined,
              engineering_input: undefined,
              theoretical_weight: null,
              ...(() => {
                const mt = normalizeMeasurementType(item.measurement_type);
                if (!mt) {
                  return { measurement_type: null, length_mm: null, width_mm: null, height_mm: null, length: '' };
                }
                const keys = measurementDimensionKeys(mt);
                return {
                  measurement_type: mt,
                  length: '',
                  length_mm: parseDimensionToMm(item.profile_length),
                  width_mm: keys.includes('width_mm') ? parseDimensionToMm(item.default_width_mm) : null,
                  height_mm: keys.includes('height_mm') ? parseDimensionToMm(item.default_height_mm) : null,
                };
              })(),
            }
          : l
      )
    );
  };

  const onSelectItem = (key: string, itemId: string, fromOption?: Item) => {
    if (!fromOption) {
      message.warning('Select an item from the list');
      return;
    }
    applyCatalogItem(key, fromOption);
  };

  const clearLineItem = (key: string) => {
    setDirectLines((prev) =>
      prev.map((l) =>
        l.key === key
          ? {
              ...l,
              item_id: undefined,
              item_code: l.line_kind === 'description_only' ? nonstockDefaultCode : undefined,
              item_name: undefined,
              item_description: l.line_kind === 'description_only' ? l.item_description : '',
              uom: undefined,
              uomOptions: undefined,
              is_critical: undefined,
              profile_grade: undefined,
              profile_heat_no: undefined,
              profile_dimension: undefined,
              profile_length: undefined,
              measurement_type: null,
              length_mm: null,
              width_mm: null,
              height_mm: null,
              engineering_input: undefined,
              theoretical_weight: null,
            }
          : l
      )
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!user?.id) {
        message.error('User not found');
        return;
      }
      const payload = {
        creation_mode: creationMode,
        pr_date: values.pr_date ? values.pr_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        required_date: values.required_date ? values.required_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        priority: values.priority || 'Normal',
        department: values.department,
        department_id: values.department_id ?? undefined,
        notes: values.notes,
        currency: values.currency ?? baseCurrency?.currency_code ?? 'AED',
        requested_by: user.id,
      };

      if (creationMode === 'Direct') {
        const incomplete = directLines.filter((l) => {
          if (!l.project_id || Number(l.requested_quantity) <= 0) return true;
          if (l.line_kind === 'description_only') {
            return !String(l.item_description || '').trim() || !String(l.uom || '').trim();
          }
          return !(l.item_id || l.item_code) || !String(l.uom || '').trim();
        });
        const validLines = directLines.filter((l) => !incomplete.includes(l));
        if (validLines.length === 0) {
          message.error(
            'Add at least one line with item (or description for Non-Stock), project, and quantity'
          );
          return;
        }
        if (incomplete.length > 0) {
          message.error(
            `${incomplete.length} line(s) are incomplete (item, project, or quantity). Fix or remove them before saving — incomplete lines are not dropped silently.`
          );
          return;
        }
        const missingProjectDesc = validLines.filter(
          (l) => l.line_kind === 'description_only' && !l.project_id
        );
        if (missingProjectDesc.length) {
          message.error('Description-only (Non-Stock) lines require a project for GRN Accrued Cost posting');
          return;
        }
        (payload as any).lines_direct = validLines.map((l, i) => ({
          id: l.id || undefined,
          line_number: i + 1,
          line_kind: l.line_kind || 'catalog',
          item_code:
            l.line_kind === 'description_only' ? l.item_code || nonstockDefaultCode : l.item_code,
          item_description: l.item_description,
          // Qty column = pieces; backend recomputes the order quantity (measured or legacy Length × Qty).
          requested_quantity: lineOrderQuantity(l),
          uom: l.uom,
          length: l.measurement_type ? null : l.length?.trim() || null,
          ...(l.measurement_type
            ? {
                piece_quantity: Number(l.requested_quantity),
                length_mm: l.length_mm ?? null,
                width_mm: l.width_mm ?? null,
                height_mm: l.height_mm ?? null,
              }
            : {}),
          estimated_unit_price: l.estimated_unit_price,
          project_id: l.project_id,
          wbs_node_id: l.wbs_node_id ?? null,
          cbs_code_id: l.cbs_code_id ?? null,
          cost_category: l.cost_category || 'Material',
          specifications: l.specifications?.trim() || null,
          notes: l.notes,
          ...(l.engineering_input ? { engineering_input: l.engineering_input } : {}),
        }));
      } else {
        const valid = selectedFromSoLines.filter((s) => Number(s.requested_quantity) > 0);
        if (valid.length === 0) {
          message.error('Select at least one line and enter request quantity');
          return;
        }
        (payload as any).lines_from_so = valid.map((s) => ({
          sales_order_id: s.line.sales_order_id,
          sales_order_line_id: s.line.sales_order_line_id,
          requested_quantity: Number(s.requested_quantity),
          project_id: s.line.project_id ?? undefined,
        }));
      }

      if (isNew) {
        await createMutation.mutateAsync(payload as any);
        navigate('/scm/procurement/purchase-requests');
      } else if (id) {
        await updateMutation.mutateAsync({ id, payload: payload as any });
        navigate(`/scm/procurement/purchase-requests/${id}`);
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    }
  };

  const toggleFromSoLine = (line: EligibleSOLine, requestQty: number) => {
    setSelectedFromSoLines((prev) => {
      const exists = prev.find((s) => s.line.sales_order_line_id === line.sales_order_line_id);
      if (exists) {
        if (requestQty <= 0) return prev.filter((s) => s.line.sales_order_line_id !== line.sales_order_line_id);
        return prev.map((s) => (s.line.sales_order_line_id === line.sales_order_line_id ? { ...s, requested_quantity: requestQty } : s));
      }
      if (requestQty <= 0) return prev;
      return [...prev, { line, requested_quantity: requestQty }];
    });
  };

  if (isEdit && prLoading && !pr) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/purchase-requests')} style={{ marginBottom: 16 }}>
        Back
      </Button>
      <Title level={4} style={{ marginBottom: 24 }}>
        {isNew ? 'Create Purchase Request' : 'Edit Purchase Request'}
      </Title>

      <Card style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ priority: 'Normal', currency: baseCurrency?.currency_code ?? 'AED' }}
        >
          {isNew && (
            <Form.Item label="Creation mode" required>
              <Radio.Group value={creationMode} onChange={(e) => setCreationMode(e.target.value)}>
                <Radio value="Direct">Direct</Radio>
                <Radio value="AgainstSalesOrder">From Customer Order</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="pr_date" label="PR Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="required_date" label="Required Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Priority">
                <Select options={[{ value: 'Low', label: 'Low' }, { value: 'Normal', label: 'Normal' }, { value: 'High', label: 'High' }, { value: 'Urgent', label: 'Urgent' }]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="currency" label="Currency" rules={[{ required: true, message: 'Select currency' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={currenciesLoading}
                  placeholder="Select currency"
                  options={currencyOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          {isEdit && pr && (
            <Form.Item label="Project">
              <Input readOnly value={headerProjectLabel(pr, pr.items)} />
            </Form.Item>
          )}
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>

          {creationMode === 'Direct' && (
            <Card title="Lines" style={{ marginBottom: 16 }}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Catalog vs description-only"
                description={
                  <Paragraph style={{ marginBottom: 0 }} type="secondary">
                    Catalog lines show Grade / Heat No / Dimension from Item Master. For items with a{' '}
                    <Text strong>Measurement type</Text>, enter <Text strong>Qty</Text> (number of
                    pieces) and the piece dimensions in mm (Length, Length × Width, or Length × Width ×
                    Height);                     <Text strong>Total qty</Text> is converted into the UOM (e.g. 6000mm × 4 →
                    24000 MM, or 6000 × 1500 mm × 4 → 36 SQM) and is what the supplier inquiry uses. The
                    purchase order quantity is the piece <Text strong>Qty</Text>, with the price converted
                    per piece so the line amount stays the same. Goods receipt stock is the total qty
                    (or a share of it when only some pieces are received). Est. Rate is per UOM (per MM /
                    SQM), not per piece. Description-only lines
                    are for Non-Stock market consumables — type a free-text description; code{' '}
                    <Text code>{nonstockDefaultCode}</Text> is attached internally. On the goods receipt,
                    a Non-Stock line can be linked to a Stock or Consumable item so the receipt posts to
                    inventory. Left unlinked, it posts to Accrued Cost (2170) with project. Same
                    PR approval workflow applies.
                  </Paragraph>
                }
              />
              <Space style={{ marginBottom: 8 }} wrap>
                <Button type="dashed" onClick={() => addDirectLine('catalog')} icon={<PlusOutlined />}>
                  Add catalog line
                </Button>
                <Button type="dashed" onClick={() => addDirectLine('description_only')} icon={<PlusOutlined />}>
                  Add description-only (Non-Stock)
                </Button>
              </Space>
              <Table
                size="small"
                dataSource={directLines}
                rowKey="key"
                pagination={false}
                scroll={{ x: 1860 }}
                tableLayout="fixed"
                rowClassName={(r) => getCriticalItemRowClassName(r)}
                expandable={(() => {
                  const spec = lineSpecificationsExpandable<DirectLine>({
                    getValue: (r) => r.specifications,
                    onChange: (r, v) => updateDirectLine(r.key, 'specifications', v),
                  });
                  return {
                    ...spec,
                    expandedRowRender: (r: DirectLine, index: number, indent: number, expanded: boolean) => (
                      <>
                        {spec.expandedRowRender?.(r, index, indent, expanded)}
                        {hasEngineering(r) && engineeringConfig ? (
                          <PrLineEngineeringPanel
                            config={engineeringConfig}
                            line={{
                              id: r.id,
                              item_code: r.item_code,
                              quantity: lineOrderQuantity(r),
                              uom: r.uom,
                              piece_quantity: r.measurement_type ? Number(r.requested_quantity) : null,
                              measurement_type: r.measurement_type ?? null,
                              length_mm: r.length_mm ?? null,
                              width_mm: r.width_mm ?? null,
                            }}
                            latest={approvedEngineering[r.item_code!]}
                            saved={r.engineering_saved ?? null}
                            input={r.engineering_input}
                            canOverride={canOverrideEngineering}
                            onInputChange={(input) => updateDirectLine(r.key, 'engineering_input', input)}
                            onPreview={(w) => updateDirectLine(r.key, 'theoretical_weight', w)}
                          />
                        ) : null}
                      </>
                    ),
                  };
                })()}
                columns={[
                  {
                    title: 'Type',
                    width: 96,
                    render: (_, r) =>
                      r.line_kind === 'description_only' ? (
                        <Text type="secondary">Non-Stock</Text>
                      ) : (
                        <Text type="secondary">Catalog</Text>
                      ),
                  },
                  {
                    title: 'Item / Description',
                    width: 300,
                    render: (_, r) =>
                      r.line_kind === 'description_only' ? (
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Input.TextArea
                            rows={2}
                            placeholder="Describe what to buy (changes every time)"
                            value={r.item_description}
                            onChange={(e) => updateDirectLine(r.key, 'item_description', e.target.value)}
                          />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Internal code: {r.item_code || nonstockDefaultCode}
                          </Text>
                          {!r.project_id ? (
                            <Text type="danger" style={{ fontSize: 12 }}>
                              Project required for GRN Accrued Cost posting
                            </Text>
                          ) : null}
                        </Space>
                      ) : (
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Space size={6} wrap style={{ width: '100%' }}>
                            <ItemSelect
                              placeholder="Search item code or name"
                              style={{ width: '100%', minWidth: 220 }}
                              value={r.item_id || undefined}
                              seedItems={lineSeedItem(r) ? [lineSeedItem(r)!] : undefined}
                              onChange={(v, option) => {
                                if (!v) {
                                  clearLineItem(r.key);
                                  return;
                                }
                                const fromOpt =
                                  !Array.isArray(option) && option
                                    ? ((option as { item?: Item }).item ??
                                      (option as { data?: { item?: Item } }).data?.item)
                                    : undefined;
                                onSelectItem(r.key, String(v), fromOpt);
                              }}
                            />
                            <CriticalItemTag critical={isItemCritical(r)} />
                          </Space>
                          {r.item_id && r.item_description ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {r.item_description}
                            </Text>
                          ) : null}
                          {!r.item_id && r.item_code ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {r.item_code}
                              {r.item_description ? ` — ${r.item_description}` : ''}
                            </Text>
                          ) : null}
                        </Space>
                      ),
                  },
                  {
                    title: 'Item Name',
                    width: 180,
                    render: (_, r) => {
                      const name =
                        r.line_kind === 'description_only'
                          ? (r.item_description || '').trim()
                          : (r.item_name || '').trim();
                      return name ? <Text>{name}</Text> : <Text type="secondary">—</Text>;
                    },
                  },
                  {
                    title: 'Dimensions (mm)',
                    width: 170,
                    render: (_, r) => {
                      if (r.measurement_type) {
                        return (
                          <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            {measurementDimensionKeys(r.measurement_type).map((k: MeasurementDimensionKey) => (
                              <InputNumber
                                key={k}
                                size="small"
                                min={0}
                                placeholder={MEASUREMENT_DIMENSION_LABELS[k]}
                                addonBefore={k === 'length_mm' ? 'L' : k === 'width_mm' ? 'W' : 'H'}
                                value={r[k] ?? undefined}
                                onChange={(v) => updateDirectLine(r.key, k, v ?? null)}
                                style={{ width: '100%' }}
                              />
                            ))}
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {MEASUREMENT_TYPE_LABELS[r.measurement_type]}
                            </Text>
                          </Space>
                        );
                      }
                      if (r.line_kind === 'catalog' && r.length) {
                        return (
                          <Input
                            placeholder="e.g. 6000mm"
                            value={r.length}
                            onChange={(e) => updateDirectLine(r.key, 'length', e.target.value)}
                            style={{ width: '100%' }}
                          />
                        );
                      }
                      return <Text type="secondary">—</Text>;
                    },
                  },
                  {
                    title: 'Qty (pcs)',
                    width: 130,
                    render: (_, r) => (
                      <InputNumber
                        min={0.0001}
                        value={r.requested_quantity}
                        onChange={(v) => updateDirectLine(r.key, 'requested_quantity', v)}
                        style={{ width: '100%' }}
                        controls
                      />
                    ),
                  },
                  {
                    title: 'Total qty',
                    width: 140,
                    render: (_, r) => {
                      if (r.measurement_type) {
                        const measured = computeMeasuredQuantity({
                          type: r.measurement_type,
                          pieces: r.requested_quantity,
                          dims: { length_mm: r.length_mm, width_mm: r.width_mm, height_mm: r.height_mm },
                          uom: r.uom,
                        });
                        const warn = measuredQuantityWarning(r.measurement_type, r.uom, measured.reason);
                        return (
                          <Text>
                            {Number(r.requested_quantity) > 0
                              ? `${measured.total}${r.uom ? ` ${r.uom}` : ''}`
                              : '—'}
                            {measured.derived && Number(r.requested_quantity) > 0 ? (
                              <div style={{ fontSize: 11, color: '#666' }}>
                                {r.requested_quantity} pcs × {measured.factor}
                                {r.uom ? ` ${r.uom}` : ''}
                              </div>
                            ) : null}
                            {warn ? (
                              <div style={{ fontSize: 11 }}>
                                <Text type="warning">{warn}</Text>
                              </div>
                            ) : null}
                          </Text>
                        );
                      }
                      const orderQty = expandQuantityByLength(
                        Number(r.requested_quantity || 0),
                        r.length,
                        r.uom
                      );
                      const len = lengthInLineUom(r.length, r.uom);
                      const showExpand =
                        r.line_kind !== 'description_only' &&
                        isLinearLengthUom(r.uom) &&
                        len != null &&
                        Number(r.requested_quantity) > 0 &&
                        orderQty !== Number(r.requested_quantity);
                      return (
                        <Text type={showExpand ? undefined : 'secondary'}>
                          {Number(r.requested_quantity) > 0
                            ? `${orderQty}${r.uom ? ` ${r.uom}` : ''}`
                            : '—'}
                          {showExpand ? (
                            <div style={{ fontSize: 11, color: '#666' }}>
                              {len} × {r.requested_quantity}
                            </div>
                          ) : null}
                        </Text>
                      );
                    },
                  },
                  {
                    title: 'UOM',
                    width: 110,
                    render: (_, r) => {
                      const opts =
                        r.line_kind === 'description_only'
                          ? masterUomCodes
                          : r.uomOptions?.length
                            ? [...new Set([...r.uomOptions, ...(r.uom ? [r.uom] : [])])]
                            : r.uom
                              ? [r.uom]
                              : masterUomCodes;
                      return (
                        <Select
                          placeholder="UOM"
                          style={{ width: '100%' }}
                          value={r.uom || undefined}
                          onChange={(v) => updateDirectLine(r.key, 'uom', v)}
                          options={opts.map((u) => ({ value: u, label: u }))}
                          showSearch
                          optionFilterProp="label"
                        />
                      );
                    },
                  },
                  ...(engineeringOn
                    ? [
                        {
                          title: 'Theo. weight',
                          width: 120,
                          render: (_: unknown, r: DirectLine) =>
                            hasEngineering(r) ? (
                              r.theoretical_weight != null ? (
                                <Text>{formatKg(r.theoretical_weight)}</Text>
                              ) : (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  Expand row to calculate
                                </Text>
                              )
                            ) : (
                              <Text type="secondary">—</Text>
                            ),
                        },
                      ]
                    : []),
                  {
                    title: `Est. Rate (${selectedCurrency})`,
                    width: 140,
                    render: (_, r) => {
                      const measured = r.measurement_type
                        ? computeMeasuredQuantity({
                            type: r.measurement_type,
                            pieces: 1,
                            dims: { length_mm: r.length_mm, width_mm: r.width_mm, height_mm: r.height_mm },
                            uom: r.uom,
                          })
                        : null;
                      const perPiece =
                        measured?.derived && Number(r.estimated_unit_price) > 0
                          ? Math.round(Number(r.estimated_unit_price) * measured.factor * 100) / 100
                          : null;
                      return (
                        <>
                          <InputNumber
                            min={0}
                            value={r.estimated_unit_price}
                            onChange={(v) => updateDirectLine(r.key, 'estimated_unit_price', v)}
                            style={{ width: '100%' }}
                          />
                          <div style={{ fontSize: 11, color: '#666' }}>
                            {r.uom ? `per ${r.uom}` : ''}
                            {perPiece != null ? ` · ≈ ${perPiece} per pc` : ''}
                          </div>
                        </>
                      );
                    },
                  },
                  {
                    title: 'Project',
                    width: 240,
                    render: (_, r) => (
                      <ProjectSelector
                        placeholder="Project"
                        allowClear
                        value={r.project_id || undefined}
                        onChange={(v) => updateDirectLine(r.key, 'project_id', v || '')}
                      />
                    ),
                  },
                  {
                    title: 'Budget / WBS',
                    width: 200,
                    render: (_, r) => (
                      <CostControlLineCell
                        projectId={r.project_id}
                        value={{
                          wbs_node_id: r.wbs_node_id,
                          cbs_code_id: r.cbs_code_id,
                          cost_category: r.cost_category,
                        }}
                        requestedAmount={lineOrderQuantity(r) * Number(r.estimated_unit_price || 0)}
                        onChange={(patch) => {
                          updateDirectLine(r.key, 'wbs_node_id', patch.wbs_node_id ?? null);
                          updateDirectLine(r.key, 'cbs_code_id', patch.cbs_code_id ?? null);
                          updateDirectLine(r.key, 'cost_category', patch.cost_category ?? 'Material');
                        }}
                      />
                    ),
                  },
                  {
                    title: '',
                    width: 72,
                    fixed: 'right',
                    render: (_, r) => (
                      <Button type="link" danger size="small" onClick={() => removeDirectLine(r.key)}>
                        Remove
                      </Button>
                    ),
                  },
                ]}
              />
            </Card>
          )}

          {creationMode === 'AgainstSalesOrder' && (
            <Card title="From Customer Order" style={{ marginBottom: 16 }}>
              <Form.Item label="Customer Orders">
                <Select
                  mode="multiple"
                  placeholder="Select one or more Customer Orders"
                  style={{ width: '100%' }}
                  value={selectedSoIds}
                  onChange={setSelectedSoIds}
                  options={salesOrders.map((so: any) => ({ value: so.id, label: `${so.sales_order_number} - ${so.customer_name}` }))}
                  filterOption={(input, opt) => (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
              {loadingEligible && <Spin />}
              {eligibleLines.length > 0 && (
                <Table
                  size="small"
                  dataSource={eligibleLines}
                  rowKey="sales_order_line_id"
                  pagination={false}
                  columns={[
                    { title: 'SO Number', dataIndex: 'sales_order_number', width: 120 },
                    { title: 'Line', dataIndex: 'line_number', width: 60 },
                    {
                      title: 'Item',
                      dataIndex: 'item_description',
                      render: (desc: string, line) => (
                        <>
                          <div>{desc}</div>
                          <LineSpecificationsText value={line.specifications} />
                        </>
                      ),
                    },
                    { title: 'Ordered', dataIndex: 'ordered_quantity', width: 80, align: 'right' },
                    { title: 'Already requested', dataIndex: 'requested_quantity', width: 100, align: 'right' },
                    { title: 'Requestable', dataIndex: 'requestable_quantity', width: 90, align: 'right' },
                    { title: 'Project', key: 'project', render: (_, r) => r.project_name ?? r.project_id ?? '-' },
                    {
                      title: 'Request Qty',
                      width: 120,
                      render: (_, line) => {
                        const sel = selectedFromSoLines.find((s) => s.line.sales_order_line_id === line.sales_order_line_id);
                        return (
                          <InputNumber
                            min={0}
                            max={line.requestable_quantity}
                            value={sel?.requested_quantity}
                            onChange={(v) => toggleFromSoLine(line, Number(v) || 0)}
                            style={{ width: '100%' }}
                            placeholder="0"
                          />
                        );
                      },
                    },
                  ]}
                />
              )}
            </Card>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending}>
                {isNew ? 'Create' : 'Update'}
              </Button>
              <Button onClick={() => navigate('/scm/procurement/purchase-requests')}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {isEdit && id && (
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_PURCHASE_REQUISITION}
          entityId={id}
          queryKeyPrefix={['documents', 'purchase-requisition']}
        />
      )}
    </div>
  );
};

export default PurchaseRequestFormPage;
